import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui";
import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import {
  useCopyFinancialTemplateMutation,
  useCreateFinancialTemplateMutation,
  useDeleteFinancialTemplateMutation,
  useGetCommunities,
  useGetFinancialTemplates,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, MoreVertical, Pencil, Trash2 } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import { errorMessage, NAME_REQUIRED, STATUS_PUBLISHED } from "./consts";
import { StatusBadge } from "./StatusBadge";

const errorToast = (e: any) => toast.error(errorMessage(e));

const useFinancialStatementVersions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = community?.communityId && community.communityId != "all" ? String(community.communityId) : undefined;
  const { data: communityList, isLoading: isCommunityLoading } = useGetCommunities({});
  const { data: versions = [], isLoading } = useGetFinancialTemplates({ communityId });

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [createError, setCreateError] = useState("");
  const [toDelete, setToDelete] = useState<any>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["financial-templates", communityId] });
  const openBuilder = (id: number | string) => navigate(String(id));

  const { mutate: create, isPending: isCreating } = useCreateFinancialTemplateMutation({
    onError: (e) => setCreateError(errorMessage(e)),
    onSuccess: (res: any) => {
      setCreateOpen(false);
      refresh();
      openBuilder(res?.data?.data?.financialTemplateId);
    },
  });
  const { mutate: copy } = useCopyFinancialTemplateMutation({
    onError: errorToast,
    onSuccess: (res: any) => {
      const v = res?.data?.data;
      toast.success(`Version ${v?.versionNo} created as a draft`);
      refresh();
      openBuilder(v?.financialTemplateId);
    },
  });
  const { mutate: remove, isPending: isDeleting } = useDeleteFinancialTemplateMutation({
    onError: errorToast,
    onSuccess: () => {
      toast.success("Draft deleted");
      setToDelete(null);
      refresh();
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...versions]
      .filter((v: any) => !q || `${v.title} ${v.description || ""}`.toLowerCase().includes(q))
      .sort((a: any, b: any) => a.title.localeCompare(b.title) || b.versionNo - a.versionNo);
  }, [versions, search]);

  const columns = [
    { accessorKey: "title", header: "Financial Version Name",
      cell: ({ row }: any) => (
        <div>
          <p className="font-medium text-primary">{row.original.title}</p>
          {row.original.description && <p className="text-xs text-gray-500">{row.original.description}</p>}
        </div>
      ) },
    { accessorKey: "versionNo", header: "Version", cell: ({ row }: any) => `v${row.original.versionNo}` },
    { accessorKey: "status", header: "Status", cell: ({ row }: any) => <StatusBadge status={row.original.status} /> },
    { accessorKey: "accountCategoryCount", header: "Categories" },
    { accessorKey: "reportingGroupCount", header: "Subcategories" },
    { accessorKey: "associatedGlCount", header: "GL Accounts" },
    { accessorKey: "updatedAt", header: "Last Updated",
      cell: ({ row }: any) => (row.original.updatedAt ? moment(row.original.updatedAt).format("MM/DD/YYYY h:mm A") : "—") },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const v = row.original;
        const isDraft = v.status !== STATUS_PUBLISHED;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-0 shadow-md" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem className="flex items-center gap-2" onClick={() => openBuilder(v.financialTemplateId)}>
                {isDraft ? <><Pencil className="w-4 h-4" /> Edit</> : <><Eye className="w-4 h-4" /> View</>}
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-2" onClick={() => copy({ financialTemplateId: String(v.financialTemplateId) })}>
                <Copy className="w-4 h-4" /> New Version
              </DropdownMenuItem>
              {isDraft && (
                <DropdownMenuItem className="flex items-center gap-2 text-red-600" onClick={() => setToDelete(v)}>
                  <Trash2 className="w-4 h-4" /> Delete Draft
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onCommunityChange = (value: any) => {
    const details = communityList?.data?.find((c: any) => c.communityId == value);
    dispatch(setCommunityData(details));
  };

  const openCreate = () => {
    setTitle("");
    setDescription("");
    setCreateError("");
    setCreateOpen(true);
  };

  const onCreate = () => {
    if (!title.trim()) {
      setCreateError(NAME_REQUIRED);
      return;
    }
    create({ communityId: Number(communityId), title: title.trim(), description: description.trim() || null });
  };

  const onDelete = () => remove({ financialTemplateId: String(toDelete.financialTemplateId) });

  return {
    community,
    communityId,
    communityList,
    isCommunityLoading,
    isLoading,
    search,
    setSearch,
    createOpen,
    setCreateOpen,
    title,
    setTitle,
    description,
    setDescription,
    createError,
    toDelete,
    setToDelete,
    rows,
    columns,
    onCommunityChange,
    openCreate,
    onCreate,
    onDelete,
    openBuilder,
    isCreating,
    isDeleting,
  };
};

export default useFinancialStatementVersions;
